import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Comment } from "../models/Comment";
import { CommentService } from "../services/CommentService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface CommentContextProps {
  comments: Comment[];
  selectedComment: Comment | null;
  setSelectedComment: (commentId: string) => void;
  createComment: (
    commentData: Omit<
      Comment,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "updatedById"
    >,
  ) => Promise<string>;
  createReply: (
    commentRefPath: string,
    commentData: Omit<
      Comment,
      "_id" | "createdAt" | "updatedAt" | "createdById" | "updatedById"
    >,
  ) => Promise<string>;
  updateComment: (
    commentId: string,
    updatedData: Partial<Omit<Comment, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteCommentById: (commentId: string) => Promise<void>;
}

const CommentContext = createContext<CommentContextProps | undefined>(
  undefined,
);

export const useComment = (): CommentContextProps => {
  const context = useContext(CommentContext);
  if (!context) {
    throw new Error("useComment must be used within a CommentProvider");
  }
  return context;
};

export const CommentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);

  const fetchComments = async () => {
    if (!currentMatterId) {
      console.error("No current matter ID found, cannot fetch comments.");
      return;
    }
    const commentsData = await MatterService.getComments(currentMatterId);
    setComments(commentsData);
  };

  useEffect(() => {
    if (!currentMatterId) return;
    // Fetch initial comments when the component mounts or currentMatterId changes
    fetchComments();
  }, [currentMatterId]);

  useSocketEventByIds(
    "comments:changed",
    ["*"], // Listen to all comment changes for the current matter
    (updatedDocument) => {
      // Callback to fetch comments when a change is detected
      console.log("Comment change detected:", updatedDocument);
      fetchComments();
    },
  );

  const setSelectedCommentById = async (commentId: string) => {
    const commentData = await CommentService.getComment(commentId);
    setSelectedComment(commentData);
  };

  const createComment = async (
    commentData: Omit<
      Comment,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
    >,
  ): Promise<string> => {
    const newCommentId = await CommentService.createComment({
      ...commentData,
      matterId: currentMatterId ?? undefined,
    });
    return newCommentId;
  };

  const createReply = async (
    commentRefPath: string,
    commentData: Omit<
      Comment,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "updatedById"
      | "matterId"
    >,
  ): Promise<string> => {
    const newCommentId = await CommentService.createReply(
      commentRefPath,
      commentData,
    );
    return newCommentId;
  };

  const updateComment = async (
    commentId: string,
    updatedData: Partial<Omit<Comment, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    await CommentService.updateComment(commentId, updatedData);
    setSelectedCommentById(commentId);
  };

  const deleteCommentById = async (commentId: string) => {
    await CommentService.deleteComment(commentId);
    setComments((prevComments) =>
      prevComments.filter((comment) => comment._id !== commentId),
    );
    if (selectedComment && selectedComment._id === commentId) {
      setSelectedComment(null);
    }
  };

  return (
    <CommentContext.Provider
      value={{
        comments,
        selectedComment,
        createComment,
        createReply,
        updateComment,
        deleteCommentById,
        setSelectedComment: setSelectedCommentById,
      }}
    >
      {children}
    </CommentContext.Provider>
  );
};
